import { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, theme, VStack } from '@bpba/ui-components';
import { ErrorBox } from '@bpba/form-components';
import axios from '../config/axiosClient';
import { toTitleCase } from '../constants/buscador';
import { fetchSucursalesInfoByLocalidad } from '../services/buscadorSucursalesApi';
import { SafeModalInfo } from '../features/turnero/components/SafeModalInfo';
import { extractApiError } from '../shared/utils/apiError';
import { downloadBlob } from '../shared/utils/downloadBlob';
import { parseSessionToken } from '../shared/utils/parseJwt';
import { extractProductosUnidad, shouldOmitSucursal } from '../shared/utils/productosUnidad';
import {
  DatosContacto,
  Horario,
  Linea,
  Localidad,
  Step,
  Sucursal,
  TipoTramite,
  TurnoCreado,
  TurnoReprogramable,
  VerificacionCliente,
} from '../model/types';

interface TurneroContextValue {
  step: Step;
  documento: string;
  cliente: VerificacionCliente | null;
  tramite: TipoTramite | null;
  linea: Linea | null;
  localidad: Localidad | null;
  sucursal: Sucursal | null;
  contacto: DatosContacto;
  fecha: string;
  horario: Horario | null;
  turno: TurnoCreado | null;

  setStep: (s: Step) => void;
  setDocumento: (d: string) => void;
  setTramite: (t: TipoTramite | null) => void;
  setLinea: (l: Linea | null) => void;
  setLocalidad: (l: Localidad | null) => void;
  setSucursal: (s: Sucursal | null) => void;
  setContacto: (c: DatosContacto) => void;
  setFecha: (f: string) => void;
  setHorario: (h: Horario | null) => void;
  resetWizard: () => void;
  resetSesion: () => void;

  verificarCliente: (doc: string, tokenCaptcha?: string, tokenSesion?: string) => Promise<VerificacionCliente>;
  obtenerTramites: () => Promise<TipoTramite[]>;
  obtenerLineas: (idTipoTurno: number) => Promise<Linea[]>;
  obtenerSucursales: (localidadId: string) => Promise<Sucursal[]>;
  obtenerFechasNoDisponibles: (
    idTipoTurnoWeb: number,
    idTipoLinea: number | undefined,
    sucursal: number,
    idAnexo: number
  ) => Promise<string[]>;
  obtenerHorarios: (
    idTipoTurnoWeb: number,
    sucursal: number,
    fecha: string,
    idAnexo: number
  ) => Promise<Horario[]>;
  sacarTurno: (contactoOverride?: DatosContacto) => Promise<TurnoCreado>;
  cancelarTurno: (doc: string) => Promise<{ ok: boolean }>;
  validarReprogramar: (idTurnoWeb: string) => Promise<TurnoReprogramable>;
  reprogramarTurno: (payload: {
    idTurnoWeb: string;
    idHorario: number;
    fecha: string;
    sucursalNombre: string;
    tramiteDescripcion: string;
    horario: string;
    email: string;
  }) => Promise<TurnoCreado>;
  reimprimirPDF: (idTurnoWeb: string) => Promise<Blob>;

  sessionToken: string | null;
  hasSessionToken: boolean;
  // Estos vienen del payload del JWT sin validacion — pueden ser cualquier
  // tipo o null. Consumidores coercionan con String() al mandar al back.
  tokenUnidad: any;
  tokenAnexoId: any;
  tokenUsuario: any;
  // true cuando el JWT trae una unidad fija (!= 0 y no null). En ese caso
  // el wizard saltea el step "sucursal" y el POST manda tokenUnidad/
  // tokenAnexoId. Con `unidad: 0` el token exige que el user elija sucursal
  // (mismo UX que el flujo publico).
  omitirSucursal: boolean;

  sucursalesDisponibles: Sucursal[];
  setSucursalesDisponibles: (s: Sucursal[]) => void;
  seleccionarSucursal: (s: Sucursal) => Promise<void>;
  validandoSucursal: boolean;
  sucursalIntento: Sucursal | null;
  clearSucursalIntento: () => void;
  loadingSucursales: boolean;
  setLoadingSucursales: (b: boolean) => void;
  sucursalError: string | null;
  clearSucursalError: () => void;

  fechasError: string | null;
  clearFechasError: () => void;

  mostrarTurnoVigente: (doc: string, turnoWeb: string, puedeReprogramar?: boolean) => void;
}

const TurneroContext = createContext<TurneroContextValue | null>(null);

export const useTurnero = () => {
  const ctx = useContext(TurneroContext);
  if (!ctx) throw new Error('useTurnero debe usarse dentro de TurneroProvider');
  return ctx;
};

const INITIAL_CONTACTO: DatosContacto = {
  email: '',
  telefono: '',
  apellidoNombre: '',
  nacimiento: '',
};
// Con token de sesion, el telefono arranca prefilleado con el prefijo
// internacional +549 (celular argentino). El usuario puede editarlo.
const INITIAL_CONTACTO_CON_TOKEN: DatosContacto = {
  email: '',
  telefono: '+549',
  apellidoNombre: '',
  nacimiento: '',
};

export const TurneroProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const dialogBtnSize = ['sm', null, null, 'md'] as const;
  const [step, setStep] = useState<Step>('documento');
  const [documento, setDocumento] = useState('');
  const [cliente, setCliente] = useState<VerificacionCliente | null>(null);
  const [tramite, setTramite] = useState<TipoTramite | null>(null);
  const [linea, setLinea] = useState<Linea | null>(null);
  const [localidad, setLocalidad] = useState<Localidad | null>(null);
  const [sucursal, setSucursal] = useState<Sucursal | null>(null);
  // Token de sesion opcional, viene por query param `?t=<jwt>` cuando el
  // turnero se embebe desde un flujo externo autenticado. Reemplaza al
  // Turnstile: si esta, se manda como `tokenSesion` en VerificacionCliente
  // y como `?t=` en el GET de Productos.
  //
  // Del payload del JWT sacamos `unidad`, `anexoId`, `usuario` para saltear
  // el paso "sucursal" del wizard y armar el POST de solicitar turno. Si el
  // decode falla o falta cualquiera de esos campos, todo cae al flujo publico
  // (como si nunca hubiera venido el `?t=`).
  const [sessionCtx] = useState(() => {
    if (typeof window === 'undefined') return parseSessionToken(null);
    return parseSessionToken(new URLSearchParams(window.location.search).get('t'));
  });
  const sessionToken = sessionCtx.token;
  const tokenUnidad = sessionCtx.unidad;
  const tokenAnexoId = sessionCtx.anexoId;
  const tokenUsuario = sessionCtx.usuario;
  const hasSessionToken = sessionToken !== null;
  // El back de `/TurnoWeb/Productos` devuelve `unidad` como contexto. Con un
  // numero != 0 la unidad esta fija → omitimos el step "sucursal". Con 0 o
  // ausente (flujo publico) el user tiene que elegirla.
  const [productosUnidad, setProductosUnidad] = useState<number | null>(null);
  const omitirSucursal = shouldOmitSucursal(productosUnidad);
  const initialContacto = hasSessionToken ? INITIAL_CONTACTO_CON_TOKEN : INITIAL_CONTACTO;
  const [contacto, setContacto] = useState<DatosContacto>(initialContacto);
  const [fecha, setFecha] = useState('');
  const [horario, setHorario] = useState<Horario | null>(null);
  const [turno, setTurno] = useState<TurnoCreado | null>(null);
  const [turnoVigente, setTurnoVigente] = useState<{
    doc: string;
    turnoWeb: string;
    puedeReprogramar: boolean;
  } | null>(null);
  // Error inline al imprimir desde el modal "turno vigente" — antes
  // salia via showError → modal global. Lo mostramos cerca del boton
  // que lo dispara.
  const [turnoVigenteError, setTurnoVigenteError] = useState<string | null>(null);
  const [sucursalesDisponibles, setSucursalesDisponibles] = useState<Sucursal[]>([]);
  const [validandoSucursal, setValidandoSucursal] = useState(false);
  // Sucursal clickeada antes de que el back confirme/rechace los turnos. La
  // UI del mapa la usa para mostrar el overlay "sin geolocalizacion" apenas
  // se hace click, sin esperar el round-trip. Persiste tras un error del back
  // (asi el overlay queda visible junto al ErrorBox); el caller debe limpiarla
  // via `clearSucursalIntento` al cambiar de localidad/contexto.
  const [sucursalIntento, setSucursalIntento] = useState<Sucursal | null>(null);
  const clearSucursalIntento = useCallback(() => setSucursalIntento(null), []);
  const [loadingSucursales, setLoadingSucursales] = useState(false);
  const [sucursalError, setSucursalError] = useState<string | null>(null);
  const clearSucursalError = useCallback(() => setSucursalError(null), []);
  const [fechasError, setFechasError] = useState<string | null>(null);
  const clearFechasError = useCallback(() => setFechasError(null), []);

  const mostrarTurnoVigente = useCallback(
    (doc: string, turnoWeb: string, puedeReprogramar = true) =>
      setTurnoVigente({ doc, turnoWeb, puedeReprogramar }),
    []
  );

  const seleccionarSucursal = useCallback(
    async (s: Sucursal) => {
      if (!tramite) return;
      setSucursalIntento(s);
      setValidandoSucursal(true);
      setSucursalError(null);
      try {
        await axios.get<any>(
          `/TurnosWeb/${tramite.idTipoTurnoWeb}/${linea?.idTipoLinea ?? 0}//Sucursal/${s.numero}/FechasNoDisponibles/${s.anexoId ?? 0}`
        );
        setSucursal(s);
        setSucursalIntento(null);
      } catch (err: any) {
        const msg = extractApiError(err);
        // El error vive en `sucursalError` y StepSucursal lo muestra
        // inline abajo del ScrollableBar. NO limpiamos `sucursalIntento` aca
        // — si era una sucursal sin geolocalizacion, el overlay del mapa se
        // mantiene visible para que el ErrorBox quede encima (no abajo de un
        // mapa "revelado" otra vez).
        setSucursalError(msg);
      } finally {
        setValidandoSucursal(false);
      }
    },
    [tramite, linea]
  );

  // Resetea los pasos del wizard pero mantiene `cliente`/`documento` —
  // el DNI se ingresa una sola vez en el EntryDniModal y persiste mientras
  // el usuario navega entre las 4 acciones (solicitar/reprogramar/etc).
  const resetWizard = useCallback(() => {
    setStep('documento');
    setTramite(null);
    setLinea(null);
    setLocalidad(null);
    setSucursal(null);
    setContacto(initialContacto);
    setFecha('');
    setHorario(null);
    setTurno(null);
  }, [initialContacto]);

  // Limpia toda la sesion (cliente + DNI + estado del wizard). Lo usa
  // EntryDniModal cuando el usuario hace "Volver al inicio" o cuando la
  // accion completo y querramos forzar re-login.
  const resetSesion = useCallback(() => {
    setStep('documento');
    setDocumento('');
    setCliente(null);
    setTramite(null);
    setLinea(null);
    setLocalidad(null);
    setSucursal(null);
    setContacto(initialContacto);
    setFecha('');
    setHorario(null);
    setTurno(null);
  }, [initialContacto]);

  const verificarCliente = useCallback(
    async (doc: string, tokenCaptcha = '', tokenSesion = '') => {
      // Cuando el turnero corre con `?t=<jwt>` no hay Turnstile — mandamos
      // `tokenCaptcha: null` (asi lo espera el back para saber que la sesion
      // vino via token en vez de captcha). Sin token, va el string del captcha.
      const usingSession = !!tokenSesion;
      // Reset de todo el estado contextual del turno al reingresar un CUIT —
      // si el user tipea otro DNI (o re-tipea el mismo despues de llegar hasta
      // Contacto), evitamos que persistan tramite/sucursal/fecha/horario/turno
      // viejos. NO tocamos el token ni los datos derivados del JWT (unidad,
      // anexo, usuario), que viven en `sessionCtx` y persisten en toda la
      // navegacion.
      setTramite(null);
      setLinea(null);
      setLocalidad(null);
      setSucursal(null);
      setFecha('');
      setHorario(null);
      setTurno(null);
      setContacto(initialContacto);
      setSucursalesDisponibles([]);
      setSucursalIntento(null);
      setSucursalError(null);
      setFechasError(null);
      const raw = (
        await axios.post<any>('/TurnoWeb/VerificacionCliente', {
          NroDocumento: doc,
          tokenCaptcha: usingSession ? null : tokenCaptcha,
          tokenSesion,
        })
      ).data;
      const data: VerificacionCliente = {
        documento: raw?.documento ?? raw?.NroDocumento ?? doc,
        apellidoNombre:
          raw?.apellidoNombre ?? raw?.nombreApellido ?? raw?.nombre ?? raw?.apellido ?? '',
        fechaNacimiento: raw?.fechaNacimiento ?? raw?.nacimiento ?? raw?.fechaNac ?? '',
        email: raw?.email ?? raw?.mail ?? '',
        telefono: raw?.telefono ?? raw?.celular ?? '',
        tokenSesion: raw?.tokenSesion,
        turnoWeb: raw?.turnoWeb ?? raw?.idTurnoWeb ?? null,
      };
      setCliente(data);
      // Prefill de los 4 campos desde la respuesta del back. Si `apellidoNombre`
      // o `fechaNacimiento` no vienen (cliente no registrado), quedan vacios y
      // StepContacto los pide al user.
      setContacto({
        email: data.email ?? '',
        telefono: data.telefono || (usingSession ? '+549' : ''),
        apellidoNombre: data.apellidoNombre ?? '',
        nacimiento: data.fechaNacimiento ?? '',
      });
      return data;
    },
    [initialContacto]
  );

  const obtenerTramites = useCallback(async () => {
    const url = sessionToken
      ? `/TurnoWeb/Productos?t=${encodeURIComponent(sessionToken)}`
      : '/TurnoWeb/Productos';
    const r = await axios.get<{ producto?: any[]; unidad?: number } | any[]>(url);
    // La response en flujo publico es un array; con token viene `{producto,
    // unidad, anexoId, usuario}`. Guardamos `unidad` porque gobierna si el
    // wizard omite el step "sucursal" (!= 0 y numero → omite).
    setProductosUnidad(extractProductosUnidad(r.data));
    const productos = Array.isArray(r.data) ? r.data : r.data.producto ?? [];
    return productos.map((p: any) => ({
      idTipoTurnoWeb: p.idTipoTurnoWeb ?? p.id,
      descripcion: p.descripcion,
      codigoTipoTurnoWeb: p.codigoTipoTurnoWeb ?? p.codigo,
      responsable: p.responsable,
      tieneLineas: p.tieneLineas ?? false,
      // El back expone plazoMaximo con varios casings dependiendo del endpoint
      // — probamos los 3 vistos: camelCase, PascalCase y el prefijado del
      // response de Reprogramar.
      plazoMaximo: p.plazoMaximo ?? p.PlazoMaximo ?? p.tipoTurnoWeb_PlazoMaximo,
    })) as TipoTramite[];
  }, [sessionToken]);

  const obtenerLineas = useCallback(async (idTipoTurno: number) => {
    const r = await axios.get<any>(`/TurnoWeb/Producto/${idTipoTurno}`);
    const lineas = Array.isArray(r.data) ? r.data : r.data.linea ?? r.data.tipoLineas ?? [];
    return lineas.map((l: any) => ({
      idTipoLinea: l.idTipoLinea ?? l.id,
      idTipoTurnoWeb: l.idTipoTurnoWeb ?? l.idTipoTw ?? idTipoTurno,
      descripcion: l.descripcion ?? l.nombre,
    })) as Linea[];
  }, []);

  const obtenerSucursales = useCallback(async (localidadId: string) => {
    // Two fetches en paralelo:
    //  - turnero: que sucursales/anexos tienen turnos (define disponibilidad)
    //  - buscador: rich info (nombre, direccion, lat/lng, horario, telefono, CP)
    // Join por `cod_ubic` (buscador) === `numero` (turnero).
    const [turneroResp, info] = await Promise.all([
      axios.get<any[]>(`/TurnoWeb/SucursalesAnexos/${localidadId}`),
      fetchSucursalesInfoByLocalidad(localidadId),
    ]);
    const infoByCodUbic = new Map(info.map((i) => [i.codUbic, i]));

    return (turneroResp.data ?? []).map((s: any) => {
      const numero = s.numero ?? s.codigoUbicacion;
      const anexoId = s.anexoId ?? s.idAnexo ?? 0;
      const extra = infoByCodUbic.get(Number(numero));
      const raw = (s.nombre ?? s.descripcion ?? '').trim();
      const tipoMatch = raw.match(/^(.*?)\s*\(([^)]+)\)\s*$/);
      const nombreFromTurnero = tipoMatch ? tipoMatch[1].trim() : raw;
      const tipoRaw = tipoMatch ? tipoMatch[2].trim() : undefined;
      const tipo = tipoRaw
        ? tipoRaw.charAt(0).toUpperCase() + tipoRaw.slice(1).toLowerCase()
        : undefined;
      // Anexos NO se enriquecen — el buscador solo indexa casas matrices
      // por cod_ubic. Cae a lo que da el turnero.
      const aplicaJoin = anexoId === 0 && !!extra;
      return {
        id: numero * 10000 + anexoId,
        numero,
        nombre: toTitleCase(aplicaJoin ? extra!.nombre || nombreFromTurnero : nombreFromTurnero),
        tipo,
        direccion: toTitleCase(aplicaJoin ? extra!.domicilio || s.direccion || '' : s.direccion || ''),
        localidad: toTitleCase(s.localidad || ''),
        anexoId,
        latitud: aplicaJoin ? extra!.latitud : undefined,
        longitud: aplicaJoin ? extra!.longitud : undefined,
        telefono: aplicaJoin ? extra!.telefono : undefined,
        horario: aplicaJoin ? extra!.horario : undefined,
        codigoPostal: aplicaJoin ? extra!.codPostal : undefined,
      };
    }) as Sucursal[];
  }, []);

  const obtenerFechasNoDisponibles = useCallback(
    async (
      idTipoTurnoWeb: number,
      idTipoLinea: number | undefined,
      sucursal: number,
      idAnexo: number
    ) => {
      // Devuelve `[]` en error para que el caller (StepFechaHora, Reprogramar)
      // apague el spinner y muestre todos los dias como disponibles en vez
      // de quedar cargando indefinidamente. El back a veces rebota este
      // endpoint por 500/timeout y no hay retry — preferimos degradar
      // silenciosamente a que la UI quede colgada.
      setFechasError(null);
      try {
        // `idTipoLinea` puede venir undefined (flujo con token — no hay
        // paso de linea). Sin el `?? 0` la URL sale como "/96/undefined//..."
        // y el back rebota con 500. Con 0 el back interpreta "sin linea".
        const r = await axios.get<any>(
          `/TurnosWeb/${idTipoTurnoWeb}/${idTipoLinea ?? 0}//Sucursal/${sucursal}/FechasNoDisponibles/${idAnexo}`
        );
        const raw = Array.isArray(r.data) ? r.data : r.data?.fecha ?? r.data?.fechas ?? [];
        return raw
          .map((f: any) => (typeof f === 'string' ? f.slice(0, 10) : f.fecha?.slice(0, 10) ?? ''))
          .filter(Boolean) as string[];
      } catch (e: any) {
        // eslint-disable-next-line no-console
        console.warn('[obtenerFechasNoDisponibles] fail:', e);
        setFechasError(extractApiError(e));
        return [];
      }
    },
    []
  );

  const obtenerHorarios = useCallback(
    async (idTipoTurnoWeb: number, sucursal: number, fecha: string, idAnexo: number) => {
      // Reusamos `fechasError` como state de error para dias+horarios: cae en
      // el mismo modal de StepFechaHora (mismo boton "Aceptar" que recarga).
      // El back a veces rebota /Horarios con `solucionable: true` (p.ej. "La
      // Fecha debe ser dentro de los proximos 60 dias") — con extractApiError
      // ese texto se muestra tal cual. Si viene sin `solucionable`, el helper
      // devuelve el fallback default.
      try {
        const r = await axios.get<any>(
          `/TurnosWeb/${idTipoTurnoWeb}/Sucursal/${sucursal}/Horarios/${fecha}/${idAnexo}`
        );
        const raw = Array.isArray(r.data) ? r.data : r.data?.horario ?? r.data?.horarios ?? [];
        return raw.map((h: any) => ({
          idHorario: h.idHorario ?? h.id,
          hora: (h.hora ?? h.horaDesde ?? h.descripcion ?? '').slice(0, 5),
          disponible: h.disponible ?? true,
        })) as Horario[];
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[obtenerHorarios] fail:', e);
        setFechasError(extractApiError(e));
        return [];
      }
    },
    []
  );

  const sacarTurno = useCallback(async (contactoOverride?: DatosContacto) => {
    const faltanBase = !cliente || !tramite || !horario || !fecha;
    const faltanUbicacion = omitirSucursal ? false : !sucursal;
    if (faltanBase || faltanUbicacion) {
      throw new Error('Faltan datos para confirmar el turno');
    }
    const c = cliente as any;
    // El override viene del onSubmit del StepContacto para evitar leer el
    // estado stale de `contacto` (setContacto se agenda pero el submit corre
    // en el mismo tick y sacarTurno cerraria sobre el valor previo).
    const datos = contactoOverride ?? contacto;
    // Con token: form-urlencoded, campos acotados y `responsable` sale del JWT.
    // Sin token: JSON como venia (nacimiento/idTipoLinea/IdEstadoTurnoWeb incluidos).
    let resp;
    // apellidoNombre y nacimiento salen del contacto (que puede venir del
    // back via VerificacionCliente, o del form cuando el user es no-cliente).
    const apellidoNombre = datos.apellidoNombre || cliente!.apellidoNombre || c.nombreApellido || '';
    const nacimiento = datos.nacimiento || cliente!.fechaNacimiento || c.nacimiento || c.fechaNac || '';
    if (hasSessionToken) {
      const body = new URLSearchParams();
      body.append('idHorario', String(horario!.idHorario));
      body.append('idTipoTurnoWeb', String(tramite!.idTipoTurnoWeb));
      // Con `unidad: 0` en el token la sucursal la eligio el user en el
      // wizard, asi que sale del state (no del JWT).
      body.append('sucursal', String(omitirSucursal ? tokenUnidad : sucursal!.numero));
      body.append('AnexoId', String(omitirSucursal ? (tokenAnexoId ?? 0) : (sucursal!.anexoId ?? 0)));
      body.append('fecha', fecha);
      body.append('documento', cliente!.documento || documento);
      body.append('apellidoNombre', apellidoNombre);
      body.append('nacimiento', nacimiento);
      body.append('email', datos.email);
      // El input muestra "+549..." pero al back le mandamos solo digitos.
      body.append('telefono', String(datos.telefono).replace(/\D/g, ''));
      if (linea?.idTipoLinea != null) body.append('idTipoLinea', String(linea.idTipoLinea));
      body.append('codigoTipoTurnoWeb', tramite!.codigoTipoTurnoWeb ?? '');
      // Cuando el JWT viene sin `usuario` (sucursal virtual), el back espera
      // el string literal "null" — asi documenta el ejemplo del contrato.
      body.append('responsable', tokenUsuario == null ? 'null' : String(tokenUsuario));
      resp = await axios.post<any>('/TurnoWeb/', body, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
    } else {
      const payload = {
        idHorario: horario!.idHorario,
        idTipoTurnoWeb: tramite!.idTipoTurnoWeb,
        sucursal: sucursal!.numero,
        AnexoId: sucursal!.anexoId ?? 0,
        fecha,
        documento: cliente!.documento || documento,
        apellidoNombre,
        nacimiento,
        email: datos.email,
        telefono: datos.telefono,
        idTipoLinea: linea?.idTipoLinea,
        IdEstadoTurnoWeb: undefined,
        codigoTipoTurnoWeb: tramite!.codigoTipoTurnoWeb,
        responsable: tramite!.responsable,
      };
      // El call site (SolicitarWizard contacto step) atrapa este error y lo
      // muestra inline arriba de los botones via `errorFecha`.
      resp = await axios.post<any>('/TurnoWeb/', payload);
    }
    const r = resp.data ?? {};
    const data: TurnoCreado = {
      idTurnoWeb: String(r.idTurnoWeb ?? r.id ?? r.idTW ?? ''),
      fecha: r.fecha ?? fecha,
      horario: r.horario ?? horario!.hora,
      sucursal: r.sucursal ?? sucursal?.nombre ?? '',
      tramite: r.tramite ?? tramite!.descripcion,
      email: r.email ?? datos.email,
    };
    setTurno(data);
    // Cuando cliente venia sin apellidoNombre/fechaNacimiento (no registrado)
    // el user los cargo en el StepContacto — los volcamos al cliente para que
    // StepConfirmacion pueda mostrar el titular sin fallback al form state.
    setCliente((prev) =>
      prev
        ? {
            ...prev,
            turnoWeb: data.idTurnoWeb,
            apellidoNombre: prev.apellidoNombre || apellidoNombre,
            fechaNacimiento: prev.fechaNacimiento || nacimiento,
          }
        : prev
    );
    return data;
  }, [
    cliente,
    tramite,
    sucursal,
    horario,
    fecha,
    contacto,
    linea,
    documento,
    hasSessionToken,
    omitirSucursal,
    tokenUnidad,
    tokenAnexoId,
    tokenUsuario,
  ]);

  const cancelarTurno = useCallback(async (doc: string) => {
    const r = await axios.delete(`/TurnoWeb/${doc}/false`);
    return { ok: r.status === 200 };
  }, []);

  const validarReprogramar = useCallback(async (idTurnoWeb: string) => {
    const validar = await axios.get<any>(`/TurnoWeb/ValidarReprogramar/${idTurnoWeb}`);
    const datos = await axios.get<any>(`/TurnoWeb/Reprogramar/${idTurnoWeb}`);
    const d = datos.data ?? {};
    const v = validar.data ?? {};

    // Intentar extraer fecha/horario del response de ValidarReprogramar
    const fecha = v.fecha || v.fechaTurno || d.fecha || '';
    const horario = v.horario || v.horaTurno || d.horario || '';

    // El endpoint Reprogramar/{id} solo trae: nombreCliente, nombreSucursal,
    // idTipoTurnoWeb, idTipoLinea, codigoUbicacion, idAnexo, tipoTurnoWeb_PlazoMaximo.
    // Productos, línea y dirección sucursal se cargan lazy en ReprogramarTurnoFlow.
    return {
      ok: true,
      idTurnoWeb,
      documento: '',
      apellidoNombre: d.nombreCliente ?? '',
      email: '',
      telefono: '',
      fecha,
      horario,
      tramiteDescripcion: '',
      idTipoTurnoWeb: d.idTipoTurnoWeb,
      idTipoLinea: d.idTipoLinea,
      sucursalId: d.codigoUbicacion,
      sucursalNumero: d.codigoUbicacion,
      sucursalNombre: String(d.nombreSucursal ?? '')
        .replace(/\s*\((SUCURSAL|DELEGACI[ÓO]N|ANEXO[^)]*|CENTRO ZONAL)\)\s*$/i, '')
        .trim(),
      sucursalDireccion: '',
      anexoId: d.idAnexo ?? 0,
      plazoMaximo: d.tipoTurnoWeb_PlazoMaximo ?? d.plazoMaximo ?? d.PlazoMaximo,
    } as TurnoReprogramable;
  }, []);

  const reprogramarTurno = useCallback(
    async (payload: {
      idTurnoWeb: string;
      idHorario: number;
      fecha: string;
      sucursalNombre: string;
      tramiteDescripcion: string;
      horario: string;
      email: string;
    }) => {
      // El call site (ReprogramarTurnoFlow) atrapa este error y lo muestra
      // inline arriba de los botones via `errorConfirmar`.
      const r = await axios.post<TurnoCreado>('/TurnoWeb/Reprogramar', {
        idTurnoWeb: payload.idTurnoWeb,
        idHorario: payload.idHorario,
        fecha: payload.fecha,
      });
      return r.data;
    },
    []
  );

  const reimprimirPDF = useCallback(async (idTurnoWeb: string) => {
    const r = await axios.post(`/TurnoWeb/ImpresionPDF?idTurnoWeb=${idTurnoWeb}`, null, {
      responseType: 'blob',
    });
    return r.data as Blob;
  }, []);

  const value = useMemo<TurneroContextValue>(
    () => ({
      step,
      documento,
      cliente,
      tramite,
      linea,
      localidad,
      sucursal,
      contacto,
      fecha,
      horario,
      turno,
      setStep,
      setDocumento,
      setTramite,
      setLinea,
      setLocalidad,
      setSucursal,
      setContacto,
      setFecha,
      setHorario,
      resetWizard,
      resetSesion,
      verificarCliente,
      obtenerTramites,
      obtenerLineas,
      obtenerSucursales,
      obtenerFechasNoDisponibles,
      obtenerHorarios,
      sacarTurno,
      cancelarTurno,
      validarReprogramar,
      reprogramarTurno,
      reimprimirPDF,
      sessionToken,
      hasSessionToken,
      tokenUnidad,
      tokenAnexoId,
      tokenUsuario,
      omitirSucursal,
      sucursalesDisponibles,
      setSucursalesDisponibles,
      seleccionarSucursal,
      validandoSucursal,
      sucursalIntento,
      clearSucursalIntento,
      loadingSucursales,
      setLoadingSucursales,
      sucursalError,
      clearSucursalError,
      fechasError,
      clearFechasError,
      mostrarTurnoVigente,
    }),
    [
      step,
      documento,
      cliente,
      tramite,
      linea,
      localidad,
      sucursal,
      contacto,
      fecha,
      horario,
      turno,
      resetWizard,
      resetSesion,
      verificarCliente,
      obtenerTramites,
      obtenerLineas,
      obtenerSucursales,
      obtenerFechasNoDisponibles,
      obtenerHorarios,
      sacarTurno,
      cancelarTurno,
      validarReprogramar,
      reprogramarTurno,
      reimprimirPDF,
      sucursalesDisponibles,
      seleccionarSucursal,
      validandoSucursal,
      sucursalIntento,
      clearSucursalIntento,
      loadingSucursales,
      sucursalError,
      clearSucursalError,
      fechasError,
      clearFechasError,
      mostrarTurnoVigente,
      hasSessionToken,
      sessionToken,
      tokenAnexoId,
      tokenUnidad,
      tokenUsuario,
      omitirSucursal,
    ]
  );

  const closeTurnoVigente = () => {
    setTurnoVigente(null);
    setTurnoVigenteError(null);
  };

  return (
    <TurneroContext.Provider value={value}>
      {children}

      <SafeModalInfo
        open={!!turnoVigente}
        title="Ya tenés un turno vigente"
        onClose={closeTurnoVigente}
        closable={false}
        footer={
          <Box
            display="flex"
            flexDirection="row"
            flexWrap="wrap"
            alignItems="center"
            justifyContent="flex-end"
            gap={`${theme.space[2]}px`}
            width="100%"
          >
            <Button
              type="button"
              variant="secondary"
              size={dialogBtnSize}
              width="fit-content"
              onClick={async () => {
                if (!turnoVigente) return;
                const { turnoWeb } = turnoVigente;
                setTurnoVigenteError(null);
                try {
                  const blob = await reimprimirPDF(turnoWeb);
                  // Antes navegabamos a /descargar tras bajar el PDF, pero
                  // eso cargaba el chunk lazy de DescargarPage y en iOS a
                  // veces fallaba con ChunkLoadError. Cerramos el modal y
                  // el user queda donde estaba. downloadBlob ya maneja iOS
                  // (abre blob en nueva pestana via window.open).
                  downloadBlob(blob, `turno-${turnoWeb}.pdf`);
                  closeTurnoVigente();
                } catch (err: any) {
                  const data = err?.response?.data;
                  const msg =
                    data?.exceptionMessage ??
                    data?.message ??
                    (typeof data === 'string' ? data : null) ??
                    err?.message ??
                    'No pudimos generar el comprobante.';
                  setTurnoVigenteError(msg);
                }
              }}
            >
              Imprimir
            </Button>
            {turnoVigente?.puedeReprogramar && (
              <Button
                type="button"
                variant="inverse"
                size={dialogBtnSize}
                width="fit-content"
                color="secondary.1"
                onClick={() => {
                  navigate('/reprogramar', { state: { autoOpen: true } });
                  closeTurnoVigente();
                }}
              >
                Reprogramar
              </Button>
            )}
            <Button
              type="button"
              variant="inverse"
              size={dialogBtnSize}
              width="fit-content"
              color="error.text"
              onClick={() => {
                navigate('/cancelar', { state: { autoOpen: true } });
                closeTurnoVigente();
              }}
            >
              Cancelar
            </Button>
          </Box>
        }
      >
        <VStack gap={3}>
          <VStack gap={2}>
            <p style={{ margin: 0, fontSize: theme.fontSizes[1], color: theme.colors.secondary[1], lineHeight: 1.5 }}>
              Encontramos un turno asociado al DNI{' '}<strong>{turnoVigente?.doc}</strong>.
            </p>
            {turnoVigente?.puedeReprogramar === false ? (
              <p style={{ margin: 0, fontSize: theme.fontSizes[1], color: theme.colors.secondary[1], lineHeight: 1.5 }}>
                Ya fue reprogramado una vez, así que no podés volver a cambiarlo — pero sí cancelarlo o imprimir el comprobante.
              </p>
            ) : (
              <p style={{ margin: 0, fontSize: theme.fontSizes[1], color: theme.colors.secondary[1], lineHeight: 1.5 }}>
                ¿Qué querés hacer?
              </p>
            )}
          </VStack>
          {turnoVigenteError && (
            <ErrorBox onClose={() => setTurnoVigenteError(null)}>{turnoVigenteError}</ErrorBox>
          )}
        </VStack>
      </SafeModalInfo>
    </TurneroContext.Provider>
  );
};

